import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import Select from 'src/components/ui/BrandSelect';
import * as yup from 'yup';
import Breadcrumb from '../components/Common/Breadcrumb';
import DeleteFacilityModal from '../components/Facilities/DeleteFacilityModal';
import FacilityMetrics from '../components/Facilities/FacilityMetrics';
import Modal from '../components/Facilities/Modal';
import { capitalizeFirstLetter, itemToOptions } from '../components/utils/GlobalFunctions';
import { flexBetweenCenter, selectStyles } from '../components/utils/ReactSelectStyles';
import { Actions as FACILITYACTIONS } from '../store/actions/facilityActions';
import { CustomOption } from '../components/Common/ReusableComponents/Reusable';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Save, Pencil, CircleHelp, Settings as SettingsIcon, Building2, Globe, Signpost, MapPin, Ruler, House, FilterX } from 'lucide-react';
import { NumericFormat } from 'react-number-format';
import { Input } from '../components/ui/input';

const Facility = ({
  facilities,
  saveFacility,
  showFacilityModal,
  facilityDataModal,
  facilityDataDeleteModal,
  branding,
  facilityOptions,
  selectedOptions,
  facilitiesPermissions,
}) => {
  const { id } = useParams();
  const { primaryColor, tooltipBackground, tooltipText } = branding?.colors;
  const [editing, setEditing] = useState(id === 'new');
  const [editedState, setEditedState] = useState(null);
  const [validationError, setValidationError] = useState({});
  const history = useHistory();
  const facility = facilities?.find((item) => item.id === id);
  const checkingForPrimaryFacility = facilities?.some((facility) => facility?.isPrimary);
  const canEdit = (facilitiesPermissions?.[id]?.edit ?? facilitiesPermissions?.['*']?.edit) || false;

  let selectedItem = {};
  selectedOptions &&
    Object.keys(selectedOptions).map(
      (x) =>
        (selectedItem[x] =
          selectedOptions[x] &&
          selectedOptions[x].find((item) => {
            return editedState && editedState?.[x] && item.value === editedState?.[x];
          })),
    );

  const addFacilityValidation = yup.object().shape({
    name: yup.string().required('Company name is required'),
    facilityTypeId: yup.string().required('Facility Type Id is required'),
    isPrimary: yup.string().required('Last name is required'),
    country: yup.string().required('Country is required'),
    postCode: yup.string().required('Post Code or External Id are required'),
  });

  const onConfirm = () => {
    const primaryFacility = facilities.find((item) => item.isPrimary);
    saveFacility({ ...primaryFacility, isPrimary: false });
    saveFacility({ ...facility, ...editedState }, history);
  };

  useEffect(() => {
    if (id === 'new') {
      setEditing(true);
      setEditedState({
        name: '',
        postCode: '',
        externalId: '',
        facilityTypeId: null,
        isPrimary: false,
        country: null,
        excludeFromReports: false,
        facilityM2: '',
        address: '',
      });
    }
  }, [id]);

  const updateField = (e) => {
    const newEditedState = { ...editedState };
    newEditedState[e.target.name] = e.target.value;
    setEditedState(newEditedState);
  };

  if (!facility && id !== 'new') {
    return (
      <div className='w-full'>
        <div className='rounded-xl border border-border bg-card p-6 shadow-sm'>
          <h2 className='text-lg font-semibold text-foreground'>Facility not Found</h2>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Breadcrumb
        items={[
          { label: 'Facilities', link: '/facilities' },
          {
            label: facility ? facility.name : 'New',
            link: `/facilities/${facility ? facility.id : 'new'}`,
          },
        ]}
      />

      <div className='w-full'>
        <div className='rounded-xl border border-border bg-card shadow-sm'>
          <div id='facilityDetails'>
            <header
              id='facilitiesHeading'
              className='flex flex-wrap items-center justify-between gap-2 border-b border-border px-6 py-4'>
              <h2 className='text-lg font-semibold text-foreground'>
                YOUR FACILITIES - {facility?.name || editedState?.name || 'New'}
              </h2>
                  {editing ? (
                    <Save
                      size={25}
                      strokeWidth={1.5}
                      className='cursor-pointer shrink-0'
                      id='save-icon'
                      onClick={async () => {
                        await addFacilityValidation
                          .validate(
                            {
                              name: editedState['name'],
                              facilityTypeId: editedState['facilityTypeId'],
                              isPrimary: editedState['isPrimary'],
                              country: editedState['country'],
                              postCode: editedState['postCode'] || editedState['externalId'],
                            },
                            { abortEarly: false },
                          )
                          .then(async () => {
                            try {
                              if (checkingForPrimaryFacility && editedState?.['isPrimary'] && !facility?.isPrimary)
                                showFacilityModal(true);
                              else {
                                await saveFacility({ ...facility, ...editedState }, history);
                                setEditing(false);
                                if (validationError) setValidationError({}); // Reset the State
                              }
                            } catch (error) {
                              console.log('error', error);
                            }
                          })
                          .catch((error) => {
                            setValidationError(error.inner);
                          });
                      }}
                    />
                  ) : (
                    canEdit && (
                      <Pencil
                        size={25}
                        strokeWidth={1.5}
                        className='cursor-pointer shrink-0'
                        onClick={() => {
                          setEditing(true);
                          setEditedState({
                            name: facility.name,
                            postCode: facility.postCode,
                            externalId: facility.externalId,
                            facilityTypeId: facility.facilityTypeId,
                            isPrimary: facility.isPrimary,
                            country: facility.country,
                            excludeFromReports: facility.excludeFromReports,
                            facilityM2: facility.facilityM2,
                            address: facility.address,
                          });
                        }}
                        id='edit-facility'
                      />
                    )
                  )}
            </header>
            <ul id='Facility-table' className='divide-y divide-border'>
                  {Object.keys(facilityOptions).map((key) => {
                    const { type, name, placeholder } = facilityOptions[key] || {};
                    const { show: showTooltip, content } = facilityOptions[key]?.tooltip || {};
                    let value;

                    // Handle Logic to show values on the UI when not on editing mode
                    if (!editing) {
                      if (type === 'select') {
                        value = _.find(selectedOptions?.[key], { value: facility?.[key] })?.label || 'N/A';
                      } else if (type === 'text') {
                        value = capitalizeFirstLetter(facility?.[key]) || 'N/A';
                      } else if (type === 'number') {
                        value = facility?.[key] || 'N/A';
                      }
                    }
                    return (
                      <li className={key} key={key}>
                        <div
                          className={`flex flex-wrap items-center justify-between gap-4 px-6 py-4 ${_.find(validationError, { path: key })?.message ? 'border-2 border-destructive rounded-lg' : ''}`}>
                          <div className='flex min-w-[150px] items-center gap-2 text-sm font-medium text-foreground'>
                            {facilityOptions[key].icon && React.createElement(facilityOptions[key].icon, { size: 16, strokeWidth: 1.5, className: 'shrink-0 text-muted-foreground' })}
                            {facilityOptions[key].label}
                            {showTooltip && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <CircleHelp size={18} strokeWidth={1.5} style={{ color: primaryColor, marginLeft: 10 }} />
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="right"
                                    className="max-w-xs"
                                    style={{ backgroundColor: tooltipBackground, color: tooltipText }}>
                                    {content}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          {editing ? (
                            <>
                              {type === 'text' && (
                                <Input
                                  type='text'
                                  id={`Facility-${name}`}
                                  name={name}
                                  placeholder={placeholder}
                                  value={editedState?.[name] || ''}
                                  onChange={updateField}
                                  className='max-w-md'
                                />
                              )}
                              {type === 'number' && (
                                <NumericFormat
                                  inputMode='numeric'
                                  id={`Facility-${name}`}
                                  name={name}
                                  placeholder={placeholder}
                                  value={editedState?.[name] || ''}
                                  onChange={updateField}
                                  className='h-9 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm'
                                  isAllowed={(values) => {
                                    const { floatValue } = values;
                                    return floatValue === undefined || floatValue < 1000000000000;
                                  }}
                                />
                              )}
                              {type === 'select' && (
                                <Select
                                  className={key}
                                  id={`Facility-${name}`}
                                  value={selectedItem?.[key] || value}
                                  options={facilityOptions?.[key].options}
                                  styles={selectStyles}
                                  isClearable
                                  menuPlacement={'auto'}
                                  components={{ Option: CustomOption('Facility') }}
                                  onChange={(selected) => {
                                    const newEditedState = { ...editedState };
                                    newEditedState[key] = selected ? selected?.value : null;
                                    setEditedState(newEditedState);
                                  }}
                                />
                              )}
                            </>
                          ) : (
                            <span className='text-sm text-muted-foreground'>{value}</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
            </ul>
          </div>

          {facility?.id && <FacilityMetrics id={id} facility={facility} />}
        </div>
      </div>

      {facilityDataModal && (
        <Modal facility={{ ...facility, ...editedState }} onConfirm={onConfirm} setEditing={setEditing} />
      )}
      {facilityDataDeleteModal?.status && <DeleteFacilityModal />}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  const facilityTypeOptions = _.sortBy(state?.facility?.types?.map(itemToOptions), ['label']);
  const countryOptions = _.sortBy(state.organisation?.countries?.map(itemToOptions), ['label']);
  const primaryFacilityOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  const excludeFromReportsOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  const selectedOptions = {
    facilityTypeId: facilityTypeOptions,
    country: countryOptions,
    isPrimary: primaryFacilityOptions,
    excludeFromReports: excludeFromReportsOptions,
  };

  const facilityOptions = {};
  facilityOptions.name = {
    label: 'Facility Name',
    name: 'name',
    type: 'text',
    icon: SettingsIcon,
    placeholder: '',
  };
  facilityOptions.facilityTypeId = {
    label: 'Facility Type',
    name: 'facilityTypeId',
    type: 'select',
    icon: Building2,
    placeholder: 'Facility Type',
    options: facilityTypeOptions,
  };
  facilityOptions.country = {
    label: 'Country',
    name: 'country',
    type: 'select',
    icon: Globe,
    placeholder: 'Country',
    options: countryOptions,
  };
  facilityOptions.postCode = {
    label: 'Post Code',
    name: 'postCode',
    type: 'text',
    icon: Signpost,
    placeholder: '',
  };
  facilityOptions.externalId = {
    label: 'External ID',
    name: 'externalId',
    type: 'text',
    icon: SettingsIcon,
    placeholder: '',
  };
  facilityOptions.address = {
    label: 'Address',
    name: 'address',
    type: 'text',
    icon: MapPin,
    placeholder: '',
  };
  facilityOptions.facilityM2 = {
    label: 'Facility m2',
    name: 'facilityM2',
    type: 'number',
    icon: Ruler,
    placeholder: '',
  };
  facilityOptions.isPrimary = {
    label: 'Primary Facility',
    name: 'isPrimary',
    type: 'select',
    icon: House,
    placeholder: 'Primary Facility',
    options: primaryFacilityOptions,
  };
  facilityOptions.excludeFromReports = {
    label: 'Exclude from Organisation Footprint',
    name: 'excludeFromReports',
    type: 'select',
    icon: FilterX,
    tooltip: {
      show: true,
      content: `Change to 'exclude' if this facility is not part of the company's consumption data and 
      should not be included in the company carbon footprint, 
      for example if this facility and data is a what-if scenario.`,
    },
    placeholder: 'Exclude from Organisation Footprint',
    options: excludeFromReportsOptions,
  };

  return {
    facilities: state?.facility?.list,
    facilityDataModal: state.facilityDataModal,
    facilityDataDeleteModal: state.facility && state.facility.facilityDataDeleteModal,
    branding: state.branding,
    facilityOptions,
    selectedOptions,
    facilitiesPermissions: state?.organisation?.permissions?.facilities,
  };
};

const mapDispatchToProps = (dispatch) => ({
  saveFacility: (facility, history) => dispatch(FACILITYACTIONS.saveFacility(facility, history)),
  showFacilityModal: (status) => dispatch(FACILITYACTIONS.showFacilityModal(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Facility);
